// MACHINERY V2 (Stroje) — V3's phone SORT ROW (M2.16b).
//
// Rendered truth: mobile/screenshots/10-v3-phone.png, viewed before this file
// was written — `ZORADIŤ · Stroj ↑ · Stav · Otv. SR`. Reference impl:
// mobile/machinery-mobile.jsx MbV3Phone (:296-302).
//
// >>> IT DRIVES THE EXISTING SORT STATE. THERE IS NO SECOND ONE. <<<
// `sort` and `onSortChange` are the page's — held by useFleetSectionState, in
// FleetPage's own instance, exactly because a body unmounts on every view
// toggle (that hook's header). So a V3 -> V1 -> V3 round trip on a phone keeps
// the ordering, rotating past 1280 hands the SAME ordering to the real table
// headers, and a group-by change resets both together. `nextSort` is the same
// same-column-flips / new-column-starts-ascending rule the desktop header uses,
// so the two surfaces cannot disagree about what a tap means.
//
// THREE CHIPS, NOT FIVE, AND THE THREE ARE NOT ARBITRARY. The table offers five
// sortable columns (status, conn, machine, location, openSr). The phone ROW
// renders only three of those values — name, status chip, open-SR pill — and
// fleetTableSort.ts's own header states the principle for the two it drops:
// "a control that orders rows by a column with no data is a lie about the data".
// A chip ordering a phone list by a connectivity dot or a location it does not
// print is the same lie, so PRIPOJ. and LOKÁCIA are not offered here. That is
// also exactly the trio the board draws.
//
// The specs are LOOKED UP in FLEET_TABLE_COLUMNS rather than restated, so the
// chips carry the same i18n keys as the table headers and a column removed
// there loses its chip here instead of crashing.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import ChipScroller from "../../mobile/ChipScroller";
import TouchChip from "../../mobile/TouchChip";
import {
  FLEET_TABLE_COLUMNS,
  FleetTableColumnSpec,
  FleetTableSort,
  FleetTableSortColumn,
} from "./fleetTableSort";

/** Board order: Stroj, Stav, Otv. SR. */
const PHONE_SORT_ORDER: readonly FleetTableSortColumn[] = ["machine", "status", "openSr"];

const phoneSortSpecs: FleetTableColumnSpec[] = PHONE_SORT_ORDER.map((c) =>
  FLEET_TABLE_COLUMNS.find((col) => col.sort === c)
).filter((c): c is FleetTableColumnSpec => !!c);

export interface FleetSortChipsProps {
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
}

const FleetSortChips: React.FC<FleetSortChipsProps> = ({ sort, onSortChange }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      {/* `.eyebrow` is the module's mono-uppercase label atom
          (machinery-v2-tokens.scss:214-220) — the same one every section label
          in v2 uses, so ZORADIŤ is not a second typographic idea. */}
      <span className="eyebrow" style={{ flexShrink: 0 }}>
        {translate("myMachinery:v2.mobile.sortBy")}
      </span>
      {/* THE WRAPPER IS LOAD-BEARING, not a stray div. ChipScroller's own root
          is `flexShrink: 0` (it is normally a full-width block), so as a direct
          flex item beside the eyebrow it would size to its MAX-CONTENT and push
          the row off the screen instead of scrolling inside it. This wrapper is
          the shrinkable item — `flex: 1` + `minWidth: 0` — and the scroller then
          fills it as a block, which is the geometry its overflow-x needs. */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <ChipScroller fadeTo="var(--bg)" gap={6} paddingInline={0} paddingBlock={0}>
          {phoneSortSpecs.map((spec) => {
            const column = spec.sort as FleetTableSortColumn;
            const active = sort.column === column;
            return (
              <TouchChip
                key={spec.key}
                active={active}
                onClick={() => onSortChange(column)}
                // NOT aria-pressed: these are one CHOICE out of a group, not three
                // independent toggles. The label carries the direction so a screen
                // reader hears what a sighted user reads off the glyph.
                ariaLabel={
                  active
                    ? translate("myMachinery:v2.mobile.sortActive", {
                        name: translate(spec.labelKey),
                        direction: translate(
                          sort.direction === "asc"
                            ? "myMachinery:v2.mobile.sortAsc"
                            : "myMachinery:v2.mobile.sortDesc"
                        ),
                      })
                    : translate("myMachinery:v2.mobile.sortByName", {
                        name: translate(spec.labelKey),
                      })
                }
                trailing={
                  active ? (
                    <span aria-hidden style={{ fontSize: 10, opacity: 0.8 }}>
                      {sort.direction === "asc" ? "↑" : "↓"}
                    </span>
                  ) : null
                }
              >
                {translate(spec.labelKey)}
              </TouchChip>
            );
          })}
        </ChipScroller>
      </div>
    </div>
  );
};

export default FleetSortChips;
